import cx from 'classnames';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { DeviceListRow } from './DeviceListRow';
import type { DeviceListTableProps, DeviceListSortKey } from './types';

/**
 * DeviceListTable - List view with sortable column headers
 */
export const DeviceListTable: React.FC<DeviceListTableProps> = ({
  app,
  devices,
  expandable,
  expandedIds,
  onToggleExpand,
  onClick,
  sortable,
  sortKey,
  sortDir = 'asc',
  onSort,
  children,
  actions,
  t,
}) => {
  const renderSortIcon = (columnKey: string) => {
    if (!sortable) return null;

    const isActive = sortKey === columnKey;
    const Icon = isActive && sortDir === 'desc' ? ChevronDown : ChevronUp;

    return (
      <span
        className={cx('ml-1 w-4 h-4', {
          'text-primary-600 dark:text-primary-400': isActive,
          'text-muted-foreground opacity-0 group-hover:opacity-100':
            !isActive,
        })}
      >
        <Icon className="w-4 h-4" />
      </span>
    );
  };

  const renderColumnHeader = (
    key: DeviceListSortKey,
    label: string,
    className?: string
  ) => {
    const isSortable = sortable && onSort;
    const isActive = sortKey === key;

    return (
      <div
        className={cx(
          'flex items-center justify-between text-sm font-semibold group',
          {
            'text-foreground': isActive,
            'text-foreground-secondary': !isActive,
            'hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer transition-colors':
              isSortable,
          },
          className
        )}
        onClick={isSortable ? () => onSort(key) : undefined}
        role={isSortable ? 'button' : undefined}
        tabIndex={isSortable ? 0 : undefined}
        onKeyDown={
          isSortable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSort(key);
                }
              }
            : undefined
        }
        aria-label={isSortable ? `${t('Sort by')} ${label}` : undefined}
        aria-sort={
          isActive
            ? sortDir === 'asc'
              ? 'ascending'
              : 'descending'
            : undefined
        }
      >
        <span>{label}</span>
        {renderSortIcon(key)}
      </div>
    );
  };

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
      data-component="DeviceListTable"
    >
      {/* Column Headers */}
      <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <div
          className={cx(
            'grid gap-4 p-4',
            expandable
              ? 'grid-cols-[3fr_2fr_48px] md:grid-cols-[2fr_1fr_1fr_1fr_48px]'
              : 'grid-cols-[3fr_2fr] md:grid-cols-[2fr_1fr_1fr_1fr]'
          )}
        >
          {/* Device Name */}
          {renderColumnHeader('deviceName', t('Device name'))}

          {/* Serial Number - Desktop only */}
          <div className="hidden md:block">
            {renderColumnHeader('serialNumber', t('Serial number'))}
          </div>

          {/* Last Active - Desktop only */}
          <div className="hidden md:block">
            {renderColumnHeader('lastSeen', t('Status for'))}
          </div>

          {/* Status */}
          {renderColumnHeader('status', t('Status'), 'justify-end')}

          {/* Toggle column (if expandable) */}
          {expandable && <div className="w-12" aria-label={t('Expand')} />}
        </div>
      </div>

      {/* Rows */}
      <div>
        {devices.map((device) => (
          <DeviceListRow
            key={device.id}
            app={app}
            device={device}
            expandable={expandable}
            isExpanded={expandedIds.has(device.id)}
            onToggleExpand={() => onToggleExpand(device.id)}
            onClick={onClick}
            actions={actions}
            t={t}
          >
            {children}
          </DeviceListRow>
        ))}
      </div>
    </div>
  );
};
